import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TextInput, Pressable } from 'react-native';
import { Mail, Plus, X } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, ON_COLOR, TYPOGRAPHY } from '../../theme';
import { useStructuresStore } from '../../store/structuresStore';
import { useAuthStore } from '../../store/authStore';
import { appAlert } from '../../utils/appAlert';
import { createPrefixedLogger } from '../../utils/logger';

const log = createPrefixedLogger('reportRecipients');

interface ReportRecipientsSectionProps {
  structureId: string;
  /**
   * Legacy fallback from the claim doc (claim.reportRecipients), shown only
   * until the owner-private subdoc loads. The subdoc is the source of truth;
   * the claim field is being retired because the claim doc is readable by
   * every authed user.
   */
  initialRecipients?: string[];
  onUpdate?: (recipients: string[]) => void;
}

export function ReportRecipientsSection({
  structureId,
  initialRecipients,
  onUpdate,
}: ReportRecipientsSectionProps) {
  const { colors } = useTheme();
  const userId = useAuthStore((s) => s.user?.id);
  const { getReportRecipients, updateReportRecipients } = useStructuresStore();
  const [recipients, setRecipients] = useState<string[]>(initialRecipients ?? []);
  const [newEmail, setNewEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);

  // This section renders only for the claim owner, who is the only
  // principal allowed to read the subdoc.
  useEffect(() => {
    let cancelled = false;
    getReportRecipients(structureId)
      .then((list) => {
        // An empty subdoc with a non-empty legacy fallback means the
        // backfill hasn't covered this claim yet — keep the fallback.
        if (!cancelled && list.length > 0) setRecipients(list);
      })
      .catch((err) => {
        // Right after a claim this read is EXPECTED to be denied, and it is
        // harmless. Firestore applies the claim write locally and fires the
        // snapshot before the server commits, so this section mounts and
        // reads while the rule's server-side
        // get(structureClaims/{id}).data.claimedBy still has no doc to read.
        // A claim that new has no recipients anyway, and the next load
        // succeeds. Anything else here is a real rules or network problem.
        if ((err as { code?: string })?.code === 'permission-denied') {
          log.warn('recipients not readable yet (claim not committed server-side)');
          return;
        }
        log.error('failed to load recipients:', err);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- store actions are stable
  }, [structureId]);

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@') || !userId) return;
    if (recipients.includes(email)) {
      setNewEmail('');
      return;
    }

    setIsSaving(true);
    const updated = [...recipients, email];
    try {
      await updateReportRecipients(structureId, userId, updated);
      setRecipients(updated);
      onUpdate?.(updated);
      setNewEmail('');
      setIsAdding(false);
    } catch {
      appAlert('Error', 'Failed to add recipient.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemove = async (email: string) => {
    if (!userId) return;
    setIsSaving(true);
    const updated = recipients.filter((r) => r !== email);
    try {
      await updateReportRecipients(structureId, userId, updated);
      setRecipients(updated);
      onUpdate?.(updated);
    } catch {
      appAlert('Error', 'Failed to remove recipient.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <View
      style={[styles.container, { backgroundColor: colors.surface, borderColor: colors.border }]}>
      <View style={styles.header}>
        <Mail size={16} color={colors.textSecondary} />
        <Text style={[styles.title, { color: colors.text }]}>Report Recipients</Text>
      </View>

      <Text style={[styles.hint, { color: colors.textTertiary }]}>
        These email addresses receive readiness reports when milestones are reached.
      </Text>

      {recipients.length > 0 && (
        <View style={styles.list}>
          {recipients.map((email) => (
            <View key={email} style={[styles.recipientRow, { borderColor: colors.border }]}>
              <Text style={[styles.recipientEmail, { color: colors.text }]}>{email}</Text>
              <Pressable
                hitSlop={8}
                onPress={() => handleRemove(email)}
                disabled={isSaving}
                accessibilityLabel={`Remove ${email}`}>
                <X size={14} color={colors.textTertiary} />
              </Pressable>
            </View>
          ))}
        </View>
      )}

      {isAdding ? (
        <View style={styles.addRow}>
          <TextInput
            style={[
              styles.emailInput,
              {
                color: colors.text,
                borderColor: isEmailFocused ? colors.borderFocus : colors.border,
              },
            ]}
            placeholder="email@example.com"
            placeholderTextColor={colors.textTertiary}
            value={newEmail}
            onChangeText={setNewEmail}
            onFocus={() => setIsEmailFocused(true)}
            onBlur={() => setIsEmailFocused(false)}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            returnKeyType="done"
            onSubmitEditing={handleAdd}
            autoFocus
          />
          <Pressable
            onPress={handleAdd}
            disabled={!newEmail.trim().includes('@') || isSaving}
            style={[
              styles.addBtn,
              {
                backgroundColor:
                  newEmail.trim().includes('@') && !isSaving ? colors.primary : colors.border,
              },
            ]}>
            <Text style={[styles.addBtnLabel, { color: ON_COLOR.text }]}>Add</Text>
          </Pressable>
        </View>
      ) : (
        <Pressable
          style={styles.addLink}
          onPress={() => setIsAdding(true)}
          accessibilityLabel="Add report recipient">
          <Plus size={14} color={colors.primary} />
          <Text style={[styles.addLinkLabel, { color: colors.primary }]}>Add recipient</Text>
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: StyleSheet.hairlineWidth,
    padding: SPACING.md,
    gap: SPACING.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  title: {
    ...TYPOGRAPHY.button,
  },
  hint: {
    ...TYPOGRAPHY.bodySmall,
    lineHeight: 16,
  },
  list: {
    gap: SPACING.xs,
  },
  recipientRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: SPACING.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  recipientEmail: {
    ...TYPOGRAPHY.body,
    flex: 1,
  },
  addRow: {
    flexDirection: 'row',
    gap: SPACING.sm,
  },
  emailInput: {
    flex: 1,
    ...TYPOGRAPHY.body,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
    outlineStyle: 'none' as any,
  },
  addBtn: {
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.sm,
    justifyContent: 'center',
  },
  addBtnLabel: {
    ...TYPOGRAPHY.button,
  },
  addLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  addLinkLabel: {
    ...TYPOGRAPHY.button,
  },
});
